import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { isAdmin } from '../lib/currentUser';
import { renderCommentHtml } from '../lib/commentRender';
import { sendEmail, commentMentionEmail, renderCommentHtmlForEmail } from '../lib/email';
import MentionPicker, { avatarPalette, initialOf } from './MentionPicker';
import EmojiPicker from './EmojiPicker';

// Brief comments — internal threaded discussion alongside Internal Notes.
// Append-only thread + composer with @-mentions. Mentions fire SendGrid
// inline at post time (2026-06 — the previous 5-min batched dispatcher
// stopped firing in production and silently queued 14 days of unsent
// emails; switched to "send now" so delivery is consistent with every
// other email in the app). The brief_comment_mention_notifications
// table is retained as audit history (every row gets sent_at + an
// optional suppressed_reason at write time), but it's no longer a
// queue — nothing scans it for unsent work.
//
// Admin-internal only (suppliers never see comments — no supplier-facing
// page joins brief_comments).
//
// Edit window: 15 min after post, by the author. Tombstones survive any
// delete (content wiped, action attribution preserved). Edits made AFTER
// the inline send cannot update an already-delivered email — typo fixes
// in the first few seconds are still safe because the send is fire-and-
// forget; if the user edits before SendGrid commits, both versions go.

const EDIT_WINDOW_MS = 15 * 60 * 1000;

// "28 May 2026, 10:14" — British, no seconds.
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
}

// The textarea shows the HUMAN form of a mention ("@Caroline Phillips ")
// while a parallel state array tracks { userId, displayName } per mention.
// On post / save, we convert the human form back into the storage sentinel
// "@[u:uuid:Display Name]" the renderer + dispatcher both speak.
//
// Insert a human-form mention at `caret`, replacing the `@…` fragment that
// triggered the picker. Returns the new value, the next caret position,
// and the mention entry the caller should append to its tracking state.
function insertMentionAt(value, caret, user) {
  const before = value.slice(0, caret);
  const after = value.slice(caret);
  const atIdx = before.lastIndexOf('@');
  if (atIdx < 0) return { value, nextCaret: caret, mention: null };
  const displayName = user.full_name || user.email || 'User';
  const inserted = `@${displayName} `;
  const next = before.slice(0, atIdx) + inserted + after;
  const nextCaret = atIdx + inserted.length;
  return { value: next, nextCaret, mention: { userId: user.id, displayName } };
}

// Convert a stored sentinel-form content back to human-form for editing.
// Returns the human-form text plus the mentions array that mirrors it
// (one entry per sentinel found, in document order).
function sentinelToHuman(content) {
  const mentions = [];
  const text = String(content || '').replace(/@\[u:([0-9a-f-]{36}):([^\]]+)\]/g, (_, uuid, name) => {
    mentions.push({ userId: uuid, displayName: name });
    return `@${name}`;
  });
  return { text, mentions };
}

// Convert human-form text + tracked mentions back into the sentinel form
// for storage. Each tracked mention consumes its first matching
// "@displayName" in the text; mentions whose name no longer appears
// (because the operator deleted or edited the chip) are silently dropped.
function humanToSentinel(text, mentions) {
  let content = String(text || '');
  const userIds = [];
  for (const m of mentions) {
    const pattern = `@${m.displayName}`;
    const idx = content.indexOf(pattern);
    if (idx === -1) continue;
    content = content.slice(0, idx) + `@[u:${m.userId}:${m.displayName}]` + content.slice(idx + pattern.length);
    userIds.push(m.userId);
  }
  return { content, userIds };
}

// "Is the textarea sitting inside an open @… fragment?" Returns either
// { query } when yes (active picker) or null when no.
function detectMentionTrigger(value, caret) {
  const before = value.slice(0, caret);
  const atIdx = before.lastIndexOf('@');
  if (atIdx < 0) return null;
  const fragment = before.slice(atIdx + 1);
  // Mentions are inline only — bail if there's a newline between @ and caret.
  if (/[\n\r]/.test(fragment)) return null;
  // Allow letters / spaces / common name characters; stop at anything else.
  if (!/^[A-Za-z][A-Za-z0-9 .'-]*$/.test(fragment) && fragment !== '') return null;
  return { query: fragment };
}

// Role pill label: prefer job_title; fall back to the role enum.
function rolePillLabel(user) {
  if (!user) return '';
  return user.job_title || (user.role === 'super_user' ? 'Super User' : (user.role || ''));
}

export default function BriefCommentsPanel({ briefId, briefDetails, currentInternalUser, onCountChange, onPosted }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState('');
  const [posting, setPosting] = useState(false);
  const [mentionTrigger, setMentionTrigger] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  // Parallel mention state: each entry { userId, displayName } pairs with
  // an "@displayName" occurrence in the textarea. Picker inserts append to
  // this; post / save convert back to the sentinel format the renderer
  // and dispatcher speak.
  const [composerMentions, setComposerMentions] = useState([]);
  const [editingMentions, setEditingMentions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [error, setError] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const composerRef = useRef(null);
  const editorRef = useRef(null);

  // Tick once a minute so the "Editable for N more min" indicator stays
  // honest without forcing a re-render storm.
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    if (!briefId) return;
    setLoading(true);
    const { data, error: e } = await supabase
      .from('brief_comments')
      .select('*, author:internal_users!author_id(id, full_name, job_title, role), deleted_by_user:internal_users!deleted_by(id, full_name)')
      .eq('brief_id', briefId)
      .order('created_at', { ascending: true });
    if (e) {
      setError(`Could not load comments: ${e.message}`);
    } else {
      setComments(data || []);
    }
    setLoading(false);
  }, [briefId]);

  useEffect(() => { load(); }, [load]);

  // Live count of non-deleted comments — drives the brief header badge.
  const liveCount = useMemo(() => comments.filter(c => !c.deleted).length, [comments]);
  useEffect(() => { onCountChange?.(liveCount); }, [liveCount, onCountChange]);

  const participantCount = useMemo(() => {
    const ids = new Set();
    for (const c of comments) if (!c.deleted && c.author_id) ids.add(c.author_id);
    return ids.size;
  }, [comments]);

  // ----- Composer behaviour -----
  const handleComposerChange = (e) => {
    const value = e.target.value;
    setComposer(value);
    const trig = detectMentionTrigger(value, e.target.selectionStart || 0);
    setMentionTrigger(trig);
    if (trig) {
      setAnchorRect(e.target.getBoundingClientRect());
    }
  };

  const handleComposerKey = (e) => {
    // Cmd/Ctrl+Enter posts; plain Enter inserts a newline (default).
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handlePost();
    }
  };

  const handlePickerSelect = (user) => {
    // Discriminate by editingId, not by which ref is passed — both refs
    // are always truthy when their corresponding textarea is mounted.
    const editing = !!editingId;
    const ta = editing ? editorRef.current : composerRef.current;
    if (!ta) return;
    const caret = ta.selectionStart || 0;
    const currentValue = editing ? editingDraft : composer;
    const { value, nextCaret, mention } = insertMentionAt(currentValue, caret, user);
    if (editing) {
      setEditingDraft(value);
      if (mention) setEditingMentions(prev => [...prev, mention]);
    } else {
      setComposer(value);
      if (mention) setComposerMentions(prev => [...prev, mention]);
    }
    setMentionTrigger(null);
    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        ta.setSelectionRange(nextCaret, nextCaret);
      }
    });
  };

  const handlePost = async () => {
    const trimmed = composer.trim();
    if (!trimmed || posting) return;
    if (!currentInternalUser?.id) {
      setError('Sign-in required to post comments.');
      return;
    }
    setPosting(true);
    setError(null);
    // Convert human-form composer text + tracked mentions into the
    // sentinel storage form. Mentions whose @displayName no longer appears
    // (because the operator deleted the chip text) are silently dropped.
    const { content: storedContent, userIds: mentionIds } = humanToSentinel(composer, composerMentions);
    const { data: inserted, error: e1 } = await supabase
      .from('brief_comments')
      .insert([{
        brief_id: briefId,
        author_id: currentInternalUser.id,
        content: storedContent,
        mention_user_ids: mentionIds,
      }])
      .select('*, author:internal_users!author_id(id, full_name, job_title, role), deleted_by_user:internal_users!deleted_by(id, full_name)')
      .single();
    if (e1 || !inserted) {
      setError(`Could not post: ${e1?.message || 'unknown error'}`);
      setPosting(false);
      return;
    }
    // 2026-06: mentions fire INLINE via SendGrid instead of going
    // through the 5-min cron dispatcher. The cron stopped firing in
    // production and queued 14 days of unsent rows silently — switch
    // to "send now" so every mention pings the recipient as soon as
    // the comment posts. Edits within the 15-min edit window can no
    // longer alter the email body (sent already), but typo fixes
    // were rare and the cost of not delivering at all is worse.
    //
    // The brief_comment_mention_notifications table is left in place
    // as legacy audit; we still WRITE a row per recipient stamped
    // sent_at=now so the table continues to log every mention sent
    // (just bypassing the queue/delay). Suppressed-reason rows are
    // also written for self / inactive / no-email cases so the
    // audit captures recipients we deliberately skipped.
    if (mentionIds.length > 0) {
      const { data: usersRow } = await supabase
        .from('internal_users')
        .select('id, full_name, is_active, email')
        .in('id', mentionIds);
      const userById = new Map((usersRow || []).map(u => [u.id, u]));
      // Brief header for the mention email. The parent passes the real
      // brief fields via briefDetails so the email renders a proper
      // Project Number / Client / Project / Brief block rather than the
      // bare UUID. window.__briefName is kept as a legacy fallback.
      const briefName = briefDetails?.briefName
        || (typeof window !== 'undefined' && window?.__briefName)
        || '';
      const projectNumber = briefDetails?.projectNumber || '';
      const client = briefDetails?.client || '';
      const projectName = briefDetails?.projectName || '';
      const briefUrl = (typeof window !== 'undefined' ? window.location.origin : '') + `/briefs/${briefId}`;
      const nowIso = new Date().toISOString();
      const createdAtDisplay = new Date(inserted.created_at).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
      const auditRows = [];
      const sendTasks = [];
      for (const uid of mentionIds) {
        const u = userById.get(uid);
        if (uid === currentInternalUser.id) {
          auditRows.push({ comment_id: inserted.id, brief_id: briefId, mentioned_user_id: uid, scheduled_for: nowIso, sent_at: nowIso, suppressed_reason: 'self_mention' });
          continue;
        }
        if (!u || u.is_active === false) {
          auditRows.push({ comment_id: inserted.id, brief_id: briefId, mentioned_user_id: uid, scheduled_for: nowIso, sent_at: nowIso, suppressed_reason: 'inactive_user' });
          continue;
        }
        if (!u.email) {
          auditRows.push({ comment_id: inserted.id, brief_id: briefId, mentioned_user_id: uid, scheduled_for: nowIso, sent_at: nowIso, suppressed_reason: 'no_email_at_dispatch' });
          continue;
        }
        // Render + send inline. Best-effort: failures get caught and
        // logged so one bad recipient doesn't block the others.
        const { subject, html } = commentMentionEmail({
          recipientName: u.full_name,
          briefName,
          projectNumber,
          client,
          projectName,
          briefUrl,
          comments: [{
            authorName: currentInternalUser.full_name || 'Fusion',
            createdAtDisplay,
            content: storedContent,
            html: renderCommentHtmlForEmail(storedContent),
          }],
        });
        sendTasks.push(
          sendEmail({ to: u.email, subject, html })
            // eslint-disable-next-line no-console
            .catch(err => console.warn('Mention email send failed', { to: u.email, error: err?.message }))
        );
        auditRows.push({ comment_id: inserted.id, brief_id: briefId, mentioned_user_id: uid, scheduled_for: nowIso, sent_at: nowIso });
      }
      // Don't block the post on email fan-out — fire-and-forget the
      // sends, but DO await the audit insert so the table reflects
      // truth before the user moves on.
      if (auditRows.length > 0) {
        await supabase.from('brief_comment_mention_notifications').insert(auditRows);
      }
      Promise.allSettled(sendTasks);
    }
    setComments(prev => [...prev, inserted]);
    setComposer('');
    setComposerMentions([]);
    setMentionTrigger(null);
    setPosting(false);
    requestAnimationFrame(() => composerRef.current?.focus());
    // Surface the post to the parent so it can fire a toast (or any
    // other side effect). Includes the mention count so the toast copy
    // can adapt — "Comment posted" vs "Comment posted · 2 people
    // notified". Optional; component still works without a handler.
    if (typeof onPosted === 'function') {
      const deliverableMentionCount = mentionIds.filter(uid => uid !== currentInternalUser.id).length;
      onPosted({ comment: inserted, mentionCount: deliverableMentionCount });
    }
  };

  // ----- Edit -----
  const enterEdit = (c) => {
    // Convert sentinel storage form to human form so the editor textarea
    // reads naturally; track mentions in parallel so saving can rebuild
    // the sentinel form for storage.
    const { text, mentions } = sentinelToHuman(c.content);
    setEditingId(c.id);
    setEditingDraft(text);
    setEditingMentions(mentions);
    setConfirmingDeleteId(null);
    requestAnimationFrame(() => {
      editorRef.current?.focus();
      editorRef.current?.setSelectionRange?.((c.content || '').length, (c.content || '').length);
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingDraft('');
    setEditingMentions([]);
    setMentionTrigger(null);
  };

  const handleEditorChange = (e) => {
    const value = e.target.value;
    setEditingDraft(value);
    const trig = detectMentionTrigger(value, e.target.selectionStart || 0);
    setMentionTrigger(trig);
    if (trig) setAnchorRect(e.target.getBoundingClientRect());
  };

  const saveEdit = async (c) => {
    const trimmed = editingDraft.trim();
    if (!trimmed) return;
    // Convert human-form editor text + tracked mentions back into the
    // sentinel storage form. No new notifications fire on edit (deliberate
    // — avoids spam from rapid re-edits).
    const { content: storedContent, userIds: mentionIds } = humanToSentinel(editingDraft, editingMentions);
    const editedAt = new Date().toISOString();
    const { error: e } = await supabase
      .from('brief_comments')
      .update({ content: storedContent, mention_user_ids: mentionIds, edited_at: editedAt })
      .eq('id', c.id)
      .eq('author_id', currentInternalUser?.id || '00000000-0000-0000-0000-000000000000');
    if (e) {
      setError(`Could not save edit: ${e.message}`);
      return;
    }
    setComments(prev => prev.map(row => row.id === c.id ? { ...row, content: storedContent, mention_user_ids: mentionIds, edited_at: editedAt } : row));
    cancelEdit();
  };

  // ----- Delete -----
  const handleDelete = async (c) => {
    const isAuthor = c.author_id === currentInternalUser?.id;
    const role = isAuthor ? 'author' : 'admin';
    // Wipe queued mention notifications first so the dispatcher doesn't fire
    // emails for a comment the user just removed.
    await supabase
      .from('brief_comment_mention_notifications')
      .delete()
      .eq('comment_id', c.id)
      .is('sent_at', null);
    const patch = {
      content: '',
      mention_user_ids: [],
      deleted: true,
      deleted_at: new Date().toISOString(),
      deleted_by: currentInternalUser?.id || null,
      deleted_by_role: role,
    };
    const { error: e } = await supabase
      .from('brief_comments')
      .update(patch)
      .eq('id', c.id);
    if (e) {
      setError(`Could not delete: ${e.message}`);
      return;
    }
    setComments(prev => prev.map(row => row.id === c.id ? {
      ...row,
      ...patch,
      deleted_by_user: { id: currentInternalUser?.id, full_name: currentInternalUser?.full_name },
    } : row));
    setConfirmingDeleteId(null);
  };

  // ----- Render helpers -----
  const renderComment = (c) => {
    const isAuthor = c.author_id === currentInternalUser?.id;
    const userIsAdmin = isAdmin(currentInternalUser);
    const inEditWindow = !c.deleted && isAuthor && (nowTs - new Date(c.created_at).getTime() < EDIT_WINDOW_MS);
    const editMinutesLeft = inEditWindow ? Math.max(0, Math.ceil((EDIT_WINDOW_MS - (nowTs - new Date(c.created_at).getTime())) / 60_000)) : 0;
    const canEdit = inEditWindow && editingId !== c.id;
    const canDelete = !c.deleted && (isAuthor || userIsAdmin) && editingId !== c.id;
    const pal = avatarPalette(c.author_id);

    // Tombstone.
    if (c.deleted) {
      return (
        <div key={c.id} className="flex items-start gap-3.5 py-4 border-b border-line last:border-0 opacity-60">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold flex-shrink-0" style={{ background: 'rgba(81,83,74,0.3)', color: '#51534A' }}>—</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-1.5">
              <span className="font-['Metropolis'] text-[13px] font-extrabold text-[#51534A]">{c.author?.full_name || 'Former user'}</span>
              {rolePillLabel(c.author) && (
                <span className="font-['Metropolis'] text-[10px] text-[#51534A] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-full bg-[#51534A]/20">{rolePillLabel(c.author)}</span>
              )}
              <span className="font-['Metropolis'] text-[11px] text-[#51534A]">{fmtDateTime(c.created_at)}</span>
            </div>
            <p className="font-['Metropolis'] text-[13px] text-[#51534A] italic">
              {c.deleted_by_role === 'admin'
                ? `Comment deleted by admin${c.deleted_by_user?.full_name ? ` (${c.deleted_by_user.full_name})` : ''} · ${fmtDateTime(c.deleted_at)}`
                : `Comment deleted by author · ${fmtDateTime(c.deleted_at)}`}
            </p>
          </div>
        </div>
      );
    }

    const isEditing = editingId === c.id;
    const wrapperClass = isEditing
      ? 'flex items-start gap-3.5 py-4 px-3 rounded-lg border border-[#E89A2E]/30'
      : 'flex items-start gap-3.5 py-4 border-b border-line last:border-0 group';
    const wrapperStyle = isEditing ? { background: 'rgba(232,154,46,0.04)' } : undefined;

    return (
      <div key={c.id} className={wrapperClass} style={wrapperStyle}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center font-['Metropolis'] font-extrabold text-[13px] flex-shrink-0" style={{ backgroundColor: pal.bg, color: pal.fg }}>
          {initialOf(c.author?.full_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
            <span className="font-['Metropolis'] text-[13px] font-extrabold text-ink">{c.author?.full_name || '—'}</span>
            {rolePillLabel(c.author) && (
              <span className="font-['Metropolis'] text-[10px] text-[#51534A] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-full bg-[#51534A]/20">{rolePillLabel(c.author)}</span>
            )}
            <span className="font-['Metropolis'] text-[11px] text-[#51534A]">{fmtDateTime(c.created_at)}</span>
            {c.edited_at && (
              <span className="font-['Metropolis'] text-[10px] text-[#51534A] italic">edited</span>
            )}
            {inEditWindow && !isEditing && (
              <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.12em] uppercase text-[#7A4E10] ml-1">Editable for {editMinutesLeft} more min</span>
            )}
            {isEditing && (
              <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.12em] uppercase text-[#7A4E10] ml-1">Editing · {editMinutesLeft} min remaining</span>
            )}
            {!isEditing && (canEdit || canDelete) && confirmingDeleteId !== c.id && (
              <div className="ml-auto flex gap-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {canEdit && (
                  <button type="button" onClick={() => enterEdit(c)} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.1em] text-[#51534A] hover:text-fusion-mid-grey">Edit</button>
                )}
                {canDelete && (
                  <button type="button" onClick={() => setConfirmingDeleteId(c.id)} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.1em] text-[#51534A] hover:text-[#E0290A]">Delete</button>
                )}
              </div>
            )}
            {confirmingDeleteId === c.id && (
              <div className="ml-auto flex items-center gap-2">
                <span className="font-['Metropolis'] text-[11px] text-fusion-mid-grey">Delete this comment?</span>
                <button type="button" onClick={() => setConfirmingDeleteId(null)} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.1em] text-[#51534A] hover:text-fusion-mid-grey">Cancel</button>
                <button type="button" onClick={() => handleDelete(c)} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.1em] text-[#E0290A] hover:text-[#ff4524]">Delete</button>
              </div>
            )}
          </div>
          {isEditing ? (
            <>
              <textarea
                ref={editorRef}
                value={editingDraft}
                onChange={handleEditorChange}
                onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); } }}
                className="w-full bg-white border border-line rounded-md text-ink font-['Metropolis'] text-[13px] px-3 py-2.5 leading-[1.5] outline-none resize-y min-h-[70px]"
                rows={3}
              />
              <div className="flex justify-end gap-2 mt-2">
                <button type="button" onClick={cancelEdit} className="bg-transparent border border-line text-fusion-mid-grey font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.1em] px-3.5 py-1.5 rounded-full">Cancel</button>
                <button type="button" onClick={() => saveEdit(c)} className="bg-[#92D400] text-[#131313] font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.1em] px-4 py-1.5 rounded-full">Save Edit</button>
              </div>
            </>
          ) : (
            <div
              className="font-['Metropolis'] text-[13px] text-fusion-mid-grey leading-[1.6] [&_strong]:text-ink [&_em]:text-ink [&_em]:italic [&_a]:text-[#2E8FA1] [&_a]:underline [&_.bc-mention]:text-[#2E8FA1] [&_.bc-mention]:bg-[#2E8FA1]/[0.12] [&_.bc-mention]:px-1.5 [&_.bc-mention]:py-px [&_.bc-mention]:rounded [&_.bc-mention]:font-bold [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: renderCommentHtml(c.content) }}
            />
          )}
        </div>
      </div>
    );
  };

  // Insert at the caret rather than appending — people reach for an emoji
  // mid-sentence, and appending to the end would need re-editing every time.
  // Selected text is replaced, which is what every other editor does.
  const insertAtCursor = (text) => {
    const el = composerRef.current;
    if (!el) { setComposer(prev => prev + text); return; }
    const start = el.selectionStart ?? composer.length;
    const end = el.selectionEnd ?? composer.length;
    const next = composer.slice(0, start) + text + composer.slice(end);
    setComposer(next);
    // Restore focus and put the caret after the inserted emoji. Deferred so it
    // runs after React has written the new value back to the textarea.
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + text.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const [collapsed, setCollapsed] = useState(false);

  const currentPal = avatarPalette(currentInternalUser?.id);

  return (
    <div className="mx-8 mt-8">
      {/* The whole section collapses. A long-running thread pushes everything
          below it off the page, and on a brief you've read before the comments
          are usually the part you're scrolling PAST. Expanded stays the default
          — a collapsed conversation is easy to forget exists — and the header
          keeps the count either way, so collapsing never hides the fact that
          there's something to read. */}
      <div className="flex items-center justify-between gap-3 mb-3.5 pb-3.5 border-b border-line">
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          aria-expanded={!collapsed}
          aria-controls="brief-comments-body"
          className="flex items-center gap-2 min-w-0 rounded group focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            className="text-[#51534A] flex-shrink-0"
            style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 150ms ease-out' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <span className="font-['Metropolis'] text-[11px] font-bold tracking-[0.22em] uppercase text-fusion-forest group-hover:text-ink transition-colors">
            Internal Comments / Notes
          </span>
        </button>
        <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.14em] uppercase text-[#51534A] flex-shrink-0">
          {liveCount} {liveCount === 1 ? 'item' : 'items'}{participantCount > 0 ? ` · ${participantCount} participant${participantCount === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {/* Errors stay visible when collapsed — a failed post must not be hidden
          by the thing you collapsed before it failed. */}
      {error && <p className="font-['Metropolis'] text-[12px] text-[#E0290A] mb-3">{error}</p>}

      <div id="brief-comments-body" hidden={collapsed}>
      {loading ? (
        <p className="font-['Metropolis'] text-[12px] text-[#51534A]">Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="font-['Metropolis'] text-[12px] text-[#51534A] mb-4">No comments or notes yet. Jot down standing context for the brief, or start a conversation below.</p>
      ) : (
        <div>{comments.map(renderComment)}</div>
      )}

      {/* Composer */}
      <div className="flex items-start gap-3.5 pt-5 mt-3.5 border-t border-line">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center font-['Metropolis'] font-extrabold text-[13px] flex-shrink-0" style={{ backgroundColor: currentPal.bg, color: currentPal.fg }}>
          {initialOf(currentInternalUser?.full_name)}
        </div>
        <div className="flex-1 min-w-0">
          <textarea
            ref={composerRef}
            value={composer}
            onChange={handleComposerChange}
            onKeyDown={handleComposerKey}
            placeholder="Add a comment or note… use @ to mention a team member"
            className="w-full bg-white border border-line rounded-lg text-ink font-['Metropolis'] text-[13px] px-3.5 py-3 leading-[1.5] outline-none resize-y min-h-[80px] focus:border-[#92D400]"
            rows={3}
          />
          <div className="flex items-center justify-between gap-3.5 mt-2.5">
            <p className="font-['Metropolis'] text-[11px] text-[#51534A] flex items-center gap-2 flex-wrap">
              <EmojiPicker onSelect={insertAtCursor} disabled={posting} />
              <span>
                <strong className="text-fusion-mid-grey">**bold**</strong> · <em className="text-fusion-mid-grey italic">*italic*</em> · <kbd className="bg-white border border-line rounded px-1.5 py-0.5 text-[10px] text-fusion-mid-grey">Cmd</kbd>+<kbd className="bg-white border border-line rounded px-1.5 py-0.5 text-[10px] text-fusion-mid-grey">Enter</kbd> to post
              </span>
            </p>
            <button
              type="button"
              onClick={handlePost}
              disabled={!composer.trim() || posting}
              className="bg-[#92D400] text-[#131313] font-['Metropolis'] text-[12px] font-extrabold uppercase tracking-[0.1em] px-5 py-2 rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {posting ? 'Posting…' : 'Post Comment'}
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Mention picker — anchored above the textarea (composer or editor). */}
      {mentionTrigger && (
        <MentionPicker
          query={mentionTrigger.query}
          anchorRect={anchorRect}
          excludeUserId={currentInternalUser?.id}
          onSelect={(user) => handlePickerSelect(user, editingId ? editorRef.current : composerRef.current)}
          onClose={() => setMentionTrigger(null)}
        />
      )}
    </div>
  );
}
